<!-- collectionOperationDashboard/component/CollectionOverview.vue -->
<template>
  <div>
    <div class="section-title">
      <div>
        <h2>{{ $t('collectionOperationDashboard.sectionTitle') }}</h2>
        <p>{{ $t('collectionOperationDashboard.sectionDesc') }}</p>
      </div>
      <div class="export-actions">
        <el-button size="small" @click="handleExport">
          {{ $t('collectionOperationDashboard.exportCityDetail') }}
        </el-button>
      </div>
    </div>

    <div class="data-contract">
      <span class="contract-label">{{ $t('collectionOperationDashboard.dataContract') }}</span>
      <span class="contract-item">{{ $t('collectionOperationDashboard.collectScan') }}</span>
      <span class="contract-split">→</span>
      <span class="contract-item">{{ $t('collectionOperationDashboard.firstLoading') }}</span>
      <span class="contract-split">→</span>
      <span class="contract-item">{{ $t('collectionOperationDashboard.closedLoop') }}</span>
      <span class="coverage-chip">{{ $t('collectionOperationDashboard.nationwideDrill') }}</span>
    </div>

    <!-- KPI 卡片 -->
    <div class="kpis">
      <div
        v-for="(kpi, index) in kpiList"
        :key="index"
        :style="{ '--tone': kpi.tone, '--accent': kpi.accent }"
        class="kpi"
      >
        <div class="label">
          <span class="label-left">
            <i class="kpi-icon">{{ kpi.icon }}</i>
            {{ $t(kpi.labelKey) }}
          </span>
          <el-tooltip :content="$t(kpi.descKey)" placement="top">
            <i class="el-icon-info help-icon" />
          </el-tooltip>
        </div>
        <strong>{{ kpi.value }}</strong>
        <small>{{ $t(kpi.descKey) }}</small>
        <!-- 右下角导出按钮：当天未完成装车 / Miss Scan -->
        <el-tooltip
          v-if="kpi.exportType"
          :content="$t('collectionOperationDashboard.exportList')"
          placement="top"
        >
          <button class="kpi-export" type="button" @click="handleKpiExport(kpi)">
            <i class="el-icon-download" />
          </button>
        </el-tooltip>
      </div>
    </div>

    <!-- 图表区域 -->
    <el-row :gutter="12" class="chart-grid">
      <el-col :md="8" :sm="24" :xs="24">
        <div class="card">
          <div class="card-head">
            <div>
              <h3>{{ $t('collectionOperationDashboard.trendTitle') }}</h3>
              <p>{{ $t('collectionOperationDashboard.trendDesc') }}</p>
            </div>
          </div>
          <div ref="trendChart" class="chart" />
        </div>
      </el-col>

      <el-col :md="8" :sm="24" :xs="24">
        <div class="card">
          <div class="card-head">
            <div>
              <h3>{{ $t('collectionOperationDashboard.customerTitle') }}</h3>
              <p>{{ $t('collectionOperationDashboard.customerDesc') }}</p>
            </div>
          </div>
          <div ref="clientChart" class="chart" />
        </div>
      </el-col>

      <el-col :md="8" :sm="24" :xs="24">
        <div class="card">
          <div class="card-head">
            <div>
              <h3>{{ $t('collectionOperationDashboard.serviceTitle') }}</h3>
              <p>W2D / D2D / SFS</p>
            </div>
          </div>
          <div ref="serviceChart" class="chart" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { normalizePlatform, platformColors } from '../utils/brazilMap';
import {
  exportFmCollectCityDetail,
  exportFmCollectMissScanList,
  exportFmCollectUnloadedList,
  getFmCollectCustomerChart,
  getFmCollectKpi,
  getFmCollectServiceChart,
  getFmCollectTrend
} from '../api';
import { goResourceTaskList } from '@/utils/goExportList';
import { formatAxisCount, formatPercent, rateAxisBounds, rateOf } from '../utils/format';

export default {
  name: 'CollectionOverview',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      charts: {},
      // 六张 KPI 卡（值由 /admin/fmCollectDashboard/kpi 接口填充）
      // label/desc 存 i18n key，切换语言时由模板实时翻译
      kpiList: [
        {
          labelKey: 'collectionOperationDashboard.kpiCollectTotal',
          descKey: 'collectionOperationDashboard.kpiCollectTotalDesc',
          key: 'collectTotal',
          value: '0',
          isPercent: false,
          icon: '▦',
          tone: '#edf8f2',
          accent: '#079455'
        },
        {
          labelKey: 'collectionOperationDashboard.kpiLoaded',
          descKey: 'collectionOperationDashboard.kpiLoadedDesc',
          key: 'loadedTotal',
          value: '0',
          isPercent: false,
          icon: '▦',
          tone: '#edf5ff',
          accent: '#2f6fed'
        },
        {
          labelKey: 'collectionOperationDashboard.kpiUnloaded',
          descKey: 'collectionOperationDashboard.kpiUnloadedDesc',
          key: 'unloadedTotal',
          value: '0',
          isPercent: false,
          icon: '▦',
          tone: '#fff5df',
          accent: '#f79009',
          // 右下角导出：当天未完成装车清单
          exportType: 'unloaded'
        },
        {
          labelKey: 'collectionCenter.CollectionRate',
          descKey: 'collectionOperationDashboard.kpiCollectRateDesc',
          key: 'collectRate',
          value: '0.00%',
          isPercent: true,
          icon: '↗',
          tone: '#edf8f2',
          accent: '#079455'
        },
        {
          labelKey: 'collectionOperationDashboard.kpiMissScan',
          descKey: 'collectionOperationDashboard.kpiMissScanDesc',
          key: 'missScanTotal',
          value: '0',
          isPercent: false,
          icon: '▦',
          tone: '#ffeded',
          accent: '#d92d20',
          // 右下角导出：Miss Scan 清单
          exportType: 'missScan'
        },
        {
          labelKey: 'collectionOperationDashboard.kpiMissScanRate',
          descKey: 'collectionOperationDashboard.kpiMissScanRateDesc',
          key: 'missScanRate',
          value: '0.00%',
          isPercent: true,
          icon: '↗',
          tone: '#f3efff',
          accent: '#6941c6'
        }
      ],
      // 图表数据（由真实接口返回）
      trendData: [],
      clientChartData: [],
      serviceChartData: []
    };
  },
  watch: {
    // 切换语言不会刷新页面，图表系列名/提示需按新语言重绘
    '$i18n.locale'() {
      this.updateCharts();
    }
  },
  mounted() {
    this.initCharts();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    Object.values(this.charts).forEach((chart) => chart.dispose());
  },
  methods: {
    // 解包 request 拦截器返回的 { code, data } 结构
    getBody(res) {
      if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'data')) {
        return res.data;
      }
      return res;
    },

    // 供父级“查询”时统一调用
    fetchData(params) {
      return Promise.all([
        this.loadKpi(params),
        this.loadTrend(params),
        this.loadCustomerChart(params),
        this.loadServiceChart(params)
      ]);
    },

    // 与页面全局筛选一致的公共查询参数
    buildQueryParams() {
      const [startDate, endDate] = this.filters.dateRange || [];
      return {
        startDate: startDate || '',
        endDate: endDate || '',
        platform: this.filters.client || '',
        serviceType: this.filters.service || '',
        stateCode: this.filters.uf || '',
        cityName: this.filters.city || '',
        // HUB 下拉对应站点，接口入参为 siteIds 数组；未选择时传空数组表示不限
        siteIds: this.filters.hub ? [this.filters.hub] : []
      };
    },

    // 异步导出提交：成功后前往资源任务列表下载
    async submitExport(exportFn, label) {
      try {
        const res = await exportFn(this.buildQueryParams());
        console.log(`[CollectionOverview] ${label}导出提交响应：`, res);
        await goResourceTaskList();
      } catch (error) {
        console.error(`[CollectionOverview] ${label}导出失败：`, error);
      }
    },

    // 导出城市明细
    handleExport() {
      return this.submitExport(
        exportFmCollectCityDetail,
        this.$t('collectionOperationDashboard.exportCityDetail')
      );
    },

    // KPI 卡右下角导出：当天未完成装车清单 / Miss Scan 清单
    handleKpiExport(kpi) {
      const exportFn =
        kpi.exportType === 'unloaded' ? exportFmCollectUnloadedList : exportFmCollectMissScanList;
      return this.submitExport(exportFn, this.$t(kpi.labelKey));
    },

    // KPI指标卡（六卡+数据更新时间），传参与 customerChart 保持一致
    async loadKpi(params) {
      try {
        const res = await getFmCollectKpi({
          startDate: params.startDate,
          endDate: params.endDate,
          platform: params.platform,
          serviceType: params.serviceType,
          stateCode: params.stateCode,
          cityName: params.cityName,
          siteIds: params.siteIds
        });
        console.log('[CollectionOverview] KPI 指标卡响应：', res);
        const body = this.getBody(res) || {};
        // 比率类指标一律用分子/分母前端重算：后端 collectRate/missScanRate 已按 1 位小数
        // 四舍五入，直接 toFixed(2) 会把 27.12% 显示成 27.10%（2026-10-26 测试反馈）
        const computedRates = {
          collectRate: rateOf(body.loadedTotal, body.collectTotal),
          missScanRate: rateOf(body.missScanTotal, body.collectTotal)
        };
        const fill = (kpi) => {
          // 无数据时不再显示「—」：比率显示 0.00%，数量显示 0
          const empty = kpi.isPercent ? '0.00%' : '0';
          const raw = body[kpi.key];
          if (kpi.isPercent) {
            // 分母为 0 时重算结果为 null，退回后端字段（通常也是 0）
            const value = computedRates[kpi.key] === null ? raw : computedRates[kpi.key];
            return formatPercent(value, 2, empty);
          }
          if (raw === undefined || raw === null || raw === '') return empty;
          const num = Number(raw);
          if (Number.isNaN(num)) return String(raw);
          return this.formatNumber(num);
        };
        this.kpiList.forEach((kpi) => {
          kpi.value = fill(kpi);
        });
        if (body.lastUpdateTime) {
          this.$emit('time-update', body.lastUpdateTime);
        }
      } catch (error) {
        console.error('[CollectionOverview] KPI 指标卡加载失败：', error);
      }
    },

    // 揽收趋势（固定最近7天不受时间筛选影响；客户/服务类型/UF/城市/HUB 筛选生效）
    async loadTrend(params) {
      try {
        const res = await getFmCollectTrend({
          startDate: params.startDate,
          endDate: params.endDate,
          platform: params.platform,
          serviceType: params.serviceType,
          stateCode: params.stateCode,
          cityName: params.cityName,
          siteIds: params.siteIds
        });
        console.log('[CollectionOverview] 揽收趋势 trend 响应：', res);
        const body = this.getBody(res);
        const list = Array.isArray(body) ? body : body && Array.isArray(body.list) ? body.list : [];
        this.trendData = list;
        this.renderTrendChart();
      } catch (error) {
        console.error('[CollectionOverview] 揽收趋势加载失败：', error);
      }
    },

    // 按客户统计揽收量（文档暂未给出具体字段，先对接并打印响应）
    async loadCustomerChart(params) {
      try {
        const res = await getFmCollectCustomerChart({
          startDate: params.startDate,
          endDate: params.endDate,
          platform: params.platform,
          serviceType: params.serviceType,
          stateCode: params.stateCode,
          cityName: params.cityName,
          siteIds: params.siteIds
        });
        console.log('[CollectionOverview] 按客户统计 customerChart 响应：', res);
        console.log('[CollectionOverview] customerChart 原始字符串：', JSON.stringify(res, null, 2));
        const body = this.getBody(res);
        const list = Array.isArray(body) ? body : body && Array.isArray(body.list) ? body.list : [];
        const arr = list.map((item) => ({
          // 后端 platform 为 TKTOK，展示统一为 TikTok
          name: normalizePlatform(
            item.platform ||
              item.platformName ||
              item.name ||
              item.client ||
              this.$t('collectionOperationDashboard.unknownClient')
          ),
          value: Number(item.collectTotal ?? item.value ?? item.total ?? 0)
        }));
        this.clientChartData = arr;
        this.renderClientChart();
      } catch (error) {
        console.error('[CollectionOverview] 按客户统计加载失败：', error);
      }
    },

    // 按服务类型统计揽收量
    async loadServiceChart(params) {
      try {
        const res = await getFmCollectServiceChart({
          startDate: params.startDate,
          endDate: params.endDate,
          platform: params.platform,
          serviceType: params.serviceType,
          stateCode: params.stateCode,
          cityName: params.cityName,
          siteIds: params.siteIds
        });
        console.log('[CollectionOverview] 按服务类型 serviceChart 响应：', res);
        const body = this.getBody(res);
        const list = Array.isArray(body) ? body : body && Array.isArray(body.list) ? body.list : [];
        this.serviceChartData = list.map((item) => ({
          name: item.name || item.serviceType || '',
          value: Number(item.collectTotal ?? item.value ?? 0)
        }));
        this.renderServiceChart();
      } catch (error) {
        console.error('[CollectionOverview] 按服务类型加载失败：', error);
      }
    },

    formatNumber(num) {
      return new Intl.NumberFormat('zh-CN').format(Math.round(num));
    },

    // 双轴 tooltip：揽收量千分位、揽收率保留 2 位（避免重算后的原始浮点直出）
    buildChartTooltip(params) {
      const list = Array.isArray(params) ? params : [params];
      if (!list.length) return '';
      const lines = list.map((item) => {
        const isRate = item.seriesName === '揽收率';
        const value =
          item.value === null || item.value === undefined
            ? '—'
            : isRate
              ? `${Number(item.value).toFixed(2)}%`
              : this.formatNumber(item.value);
        return `${item.marker}${item.seriesName}：${value}`;
      });
      return [list[0].axisValue, ...lines].join('<br/>');
    },

    initCharts() {
      this.charts.trend = echarts.init(this.$refs.trendChart);
      this.charts.client = echarts.init(this.$refs.clientChart);
      this.charts.service = echarts.init(this.$refs.serviceChart);

      this.updateCharts();
    },

    getBaseOption() {
      return {
        animationDuration: 720,
        animationEasing: 'cubicOut',
        color: platformColors,
        textStyle: {
          fontFamily: 'Inter, PingFang SC, Microsoft YaHei, sans-serif',
          color: '#52665c'
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(16,35,27,.96)',
          borderColor: 'rgba(255,255,255,.12)',
          borderWidth: 1,
          padding: [10, 12],
          textStyle: { color: '#fff', fontSize: 12 },
          axisPointer: {
            type: 'line',
            lineStyle: { color: '#91cdb0', type: 'dashed' }
          },
          confine: true
        },
        grid: {
          left: 54,
          right: 36,
          top: 48,
          bottom: 48
        }
      };
    },

    updateCharts() {
      this.renderTrendChart();
      this.renderClientChart();
      this.renderServiceChart();
    },

    renderTrendChart() {
      const rows = Array.isArray(this.trendData) ? this.trendData : [];
      const dates = rows.map((item) => String(item.statDate || '').slice(5) || '--');
      const rates = rows.map((item) => {
        // 优先用分子/分母重算，避免后端 1 位小数带来的精度误差
        const computed = rateOf(item.loadedTotal, item.collectTotal);
        if (computed !== null) return computed;
        const r = item.collectRate;
        return r === '' || r === null || r === undefined ? null : Number(r);
      });
      const rateAxis = rateAxisBounds(rates);

      const base = this.getBaseOption();
      const option = {
        ...base,
        // 揽收率已改为前端重算，原始浮点不能被 tooltip 直出
        tooltip: { ...base.tooltip, formatter: (params) => this.buildChartTooltip(params) },
        legend: { top: 8 },
        xAxis: {
          type: 'category',
          data: dates
        },
        yAxis: [
          {
            type: 'value',
            // 295 这种量级统一 /1000 会被抹成 0k，交给公共格式化按量级判断
            // minInterval=1：小范围（如 0~3 件）不出现 0.5 步长，避免取整后刻度重复
            minInterval: 1,
            axisLabel: { formatter: (v) => formatAxisCount(v) }
          },
          {
            type: 'value',
            min: rateAxis.min,
            max: rateAxis.max,
            axisLabel: { formatter: (v) => `${Math.round(v * 10) / 10}%` }
          }
        ],
        series: [
          {
            name: this.$t('collectionOperationDashboard.collectVolume'),
            type: 'bar',
            barMaxWidth: 24,
            showBackground: true,
            backgroundStyle: {
              color: '#f1f5f3',
              borderRadius: [6, 6, 0, 0]
            },
            data: rows.map((item) => Number(item.collectTotal) || 0),
            itemStyle: {
              borderRadius: [6, 6, 0, 0],
              color: '#079455'
            }
          },
          {
            name: this.$t('collectionCenter.CollectionRate'),
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbolSize: 7,
            // 揽收率折线固定蓝色 #2f6fed，与柱状揽收量（绿色）区分
            itemStyle: { color: '#2f6fed' },
            lineStyle: { width: 3, color: '#2f6fed' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(47,111,237,.22)' },
                { offset: 1, color: 'rgba(47,111,237,0)' }
              ])
            },
            data: rates
          }
        ]
      };

      this.charts.trend.setOption(option, true);
    },

    renderClientChart() {
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: (params) =>
            `${params.name}<br/>${this.formatNumber(params.value)}${this.$t(
              'collectionOperationDashboard.piece'
            )} · ${params.percent}%`
        },
        legend: { bottom: 10 },
        color: platformColors,
        series: [
          {
            type: 'pie',
            radius: ['48%', '72%'],
            center: ['50%', '44%'],
            padAngle: 2,
            itemStyle: {
              borderRadius: 8,
              borderColor: '#fff',
              borderWidth: 3
            },
            label: {
              formatter: '{b}\n{d}%'
            },
            data: this.clientChartData
          }
        ]
      };

      this.charts.client.setOption(option, true);
    },

    renderServiceChart() {
      const data = Array.isArray(this.serviceChartData) ? this.serviceChartData : [];
      const option = {
        ...this.getBaseOption(),
        grid: {
          left: 60,
          right: 45,
          top: 24,
          bottom: 35
        },
        xAxis: { type: 'value' },
        yAxis: {
          type: 'category',
          data: data.map((item) => item.name)
        },
        series: [
          {
            type: 'bar',
            barWidth: 24,
            showBackground: true,
            backgroundStyle: {
              color: '#f1f5f3',
              borderRadius: 8
            },
            label: {
              show: true,
              position: 'right',
              formatter: (p) => this.formatNumber(p.value)
            },
            data: data.map((item, index) => ({
              value: item.value,
              itemStyle: {
                color: platformColors[index],
                borderRadius: [0, 8, 8, 0]
              }
            }))
          }
        ]
      };

      this.charts.service.setOption(option, true);
    },

    handleResize() {
      Object.values(this.charts).forEach((chart) => chart.resize());
    }
  }
};
</script>

<style lang="scss" scoped>
.section-title {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  margin: 4px 0 13px;
  padding-left: 12px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 2px;
    bottom: 2px;
    width: 3px;
    border-radius: 2px;
    background: linear-gradient(#079455, #4ad08d);
  }

  h2 {
    font-size: 18px;
    margin: 0;
    letter-spacing: -0.2px;
  }

  p {
    margin: 3px 0 0;
    color: #76877f;
    font-size: 12px;
  }
}

.export-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.data-contract {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: -8px 0 16px;
  padding: 10px 13px;
  border: 1px solid #e1e9e5;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.78);
  color: #667a70;
  font-size: 11px;

  .contract-label {
    font-weight: 900;
    color: #05603a;
    letter-spacing: 0.2px;
  }

  .contract-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #8fd4b0;
    }
  }

  .contract-split {
    color: #c0cbc5;
  }

  .coverage-chip {
    margin-left: auto;
    padding: 4px 8px;
    border-radius: 999px;
    background: #eaf8f1;
    color: #05603a;
    font-weight: 800;
  }
}

.kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(145px, 1fr));
  gap: 12px;
  margin-bottom: 12px;

  .kpi {
    min-height: 130px;
    border: 1px solid #e1e9e5;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(16, 36, 27, 0.03), 0 12px 32px rgba(16, 36, 27, 0.055);
    padding: 15px 16px;
    position: relative;
    overflow: hidden;
    transition: 0.22s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 4px rgba(16, 36, 27, 0.04), 0 18px 42px rgba(16, 36, 27, 0.09);
      border-color: #d3e3db;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0 0 auto;
      height: 3px;
      background: var(--accent, #079455);
    }

    &::after {
      content: '';
      position: absolute;
      right: -26px;
      bottom: -44px;
      width: 118px;
      height: 118px;
      border-radius: 50%;
      background: var(--tone, #edf8f2);
      opacity: 0.75;
    }

    .label {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #61736a;
      font-size: 12px;
      font-weight: 700;
      position: relative;
      z-index: 1;

      .label-left {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .kpi-icon {
        width: 29px;
        height: 29px;
        display: grid;
        place-items: center;
        border-radius: 9px;
        background: var(--tone, #edf8f2);
        color: var(--accent, #079455);
        font-weight: 900;
      }

      .help-icon {
        width: 20px;
        height: 20px;
        border: 0;
        border-radius: 7px;
        background: #f4f7f5;
        color: #85958d;
        font-size: 12px;
        display: grid;
        place-items: center;
        cursor: help;
      }
    }

    strong {
      display: block;
      font-size: 28px;
      line-height: 1.12;
      letter-spacing: -0.7px;
      margin: 13px 0 6px;
      position: relative;
      z-index: 1;
    }

    small {
      color: #76877f;
      font-size: 11px;
      position: relative;
      z-index: 1;
    }

    // 右下角导出按钮（当天未完成装车 / Miss Scan）
    .kpi-export {
      position: absolute;
      right: 12px;
      bottom: 10px;
      z-index: 2;
      width: 26px;
      height: 26px;
      padding: 0;
      display: grid;
      place-items: center;
      border: 1px solid #dbe5e0;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.92);
      color: #61736a;
      font-size: 14px;
      cursor: pointer;
      transition: 0.18s ease;

      &:hover {
        border-color: #079455;
        background: #fff;
        color: #05603a;
        box-shadow: 0 4px 12px rgba(7, 148, 85, 0.18);
      }
    }
  }
}

.chart-grid {
  margin-bottom: 12px;

  .card {
    border: 1px solid #e1e9e5;
    border-radius: 17px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(16, 36, 27, 0.03), 0 12px 32px rgba(16, 36, 27, 0.055);
    min-width: 0;
    transition: 0.22s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 4px rgba(16, 36, 27, 0.04), 0 18px 42px rgba(16, 36, 27, 0.09);
      border-color: #d3e3db;
    }

    .card-head {
      padding: 16px 17px 3px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;

      h3 {
        font-size: 15px;
        margin: 0;
        letter-spacing: -0.1px;
      }

      p {
        font-size: 11px;
        color: #76877f;
        margin: 3px 0;
      }
    }

    .chart {
      height: 320px;
    }
  }
}

@media (max-width: 1500px) {
  .kpis {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1050px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .chart-grid {
    .el-col {
      margin-bottom: 12px;
    }
  }
}

@media (max-width: 700px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
